// ParsedMeshBlock → THREE.BufferGeometry。
//
// ブレンドシェイプは Three.js 標準の morphAttributes（DataArrayTexture の layer 方向に
// シェイプキーを積む方式）を使わない。標準機構は GPU の MAX_ARRAY_TEXTURE_LAYERS
// （WebGL2 保証値 256）に縛られ、VRChat アバターでよくある 256 超のシェイプキーで
// 描画が破綻しうるため。代わりに 2D DataTexture の height 方向にシェイプキーを積む
// 自前方式（buildShapeDeltaTextures）を使い、liltoon.ts の頂点シェーダで加算する。
// さらに頂点数が MAX_TEXTURE_SIZE（テクスチャ幅の上限）を超えるメッシュに対応するため、
// 頂点方向は texWidth=min(vertexCount, maxTextureSize) で複数行に折り返す 2D タイル化を行う
// （row=floor(vertexIndex/texWidth), col=vertexIndex%texWidth）。
// 実質上限はテクスチャ高さ blendshapeCount*rowsPerShape が MAX_TEXTURE_SIZE を超えないこと。

import * as THREE from "three";
import type { FaceMeshInfo, ParsedMeshBlock } from "../core/types.ts";
import { flipWinding, flipZTangent, flipZVec3 } from "./convert.ts";

export function buildGeometry(block: ParsedMeshBlock, info: FaceMeshInfo): THREE.BufferGeometry {
  const geo = new THREE.BufferGeometry();
  geo.name = info.name;

  geo.setAttribute("position", new THREE.BufferAttribute(flipZVec3(block.positions), 3));
  geo.setAttribute("normal", new THREE.BufferAttribute(flipZVec3(block.normals), 3));
  geo.setAttribute("tangent", new THREE.BufferAttribute(flipZTangent(block.tangents), 4));
  geo.setAttribute("uv", new THREE.BufferAttribute(block.uv0.slice(), 2));
  geo.setIndex(new THREE.BufferAttribute(flipWinding(block.indices), 1));

  // サブメッシュ → group（materialIndex はサブメッシュの並び位置）
  for (let i = 0; i < block.submeshes.length; i++) {
    const sm = block.submeshes[i];
    geo.addGroup(sm.indexOffset, sm.indexCount, i);
  }

  geo.computeBoundingBox();
  geo.computeBoundingSphere();
  return geo;
}

/** 1 メッシュぶんのシェイプデルタテクスチャ一式 */
export interface ShapeDeltaTextures {
  /** texel(x=col, y=shapeIndex*rowsPerShape+row).rgb = 位置デルタ（頂点方向は 2D タイル化） */
  posTex: THREE.DataTexture;
  /** texel(x=col, y=shapeIndex*rowsPerShape+row).rgb = 法線デルタ */
  nrmTex: THREE.DataTexture;
  /** シェイプキー数 */
  count: number;
  /** 頂点数 */
  vertexCount: number;
  /** テクスチャ幅（= min(vertexCount, maxTextureSize)） */
  texWidth: number;
  /** シェイプキー 1 個あたりの行数（= ceil(vertexCount / texWidth)） */
  rowsPerShape: number;
  dispose(): void;
}

function makeDeltaTexture(data: Float32Array<ArrayBuffer>, w: number, h: number): THREE.DataTexture {
  const tex = new THREE.DataTexture(data, w, h, THREE.RGBAFormat, THREE.FloatType);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.generateMipmaps = false;
  tex.flipY = false;
  tex.colorSpace = THREE.NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/**
 * スパースなブレンドシェイプデルタを 2D テクスチャ（RGBA Float）に展開する。
 * シェイプキー 0 個なら null。
 *
 * 頂点数が maxTextureSize を超える場合、頂点方向を複数行に折り返してタイル化する
 * （texWidth = min(vertexCount, maxTextureSize)、rowsPerShape = ceil(vertexCount / texWidth)）。
 * シェイプキーごとの領域は y = shapeIndex*rowsPerShape 〜 (shapeIndex+1)*rowsPerShape-1 の行に積む。
 *
 * @param maxTextureSize renderer.capabilities.maxTextureSize。高さ側が超過した場合は例外。
 */
export function buildShapeDeltaTextures(
  block: ParsedMeshBlock,
  maxTextureSize = Infinity,
): ShapeDeltaTextures | null {
  const count = block.blendshapeCount;
  const vertexCount = block.vertexCount;
  if (count <= 0 || vertexCount <= 0) return null;

  const texWidth = Math.min(vertexCount, maxTextureSize);
  const rowsPerShape = Math.ceil(vertexCount / texWidth);
  const texHeight = count * rowsPerShape;
  if (texWidth > maxTextureSize || texHeight > maxTextureSize) {
    throw new Error(
      `メッシュ "${block.meshIndex}" のシェイプデルタテクスチャ(${texWidth}x${texHeight})が` +
        `この端末の最大テクスチャサイズ(${maxTextureSize})を超えています。`,
    );
  }

  const pos = new Float32Array(new ArrayBuffer(texWidth * texHeight * 4 * 4));
  const nrm = new Float32Array(new ArrayBuffer(texWidth * texHeight * 4 * 4));
  for (let s = 0; s < count; s++) {
    const shape = block.blendshapes[s];
    if (!shape) continue;
    const shapeRowOffset = s * rowsPerShape * texWidth * 4;
    for (let d = 0; d < shape.indices.length; d++) {
      const vIdx = shape.indices[d];
      const col = vIdx % texWidth;
      const row = Math.floor(vIdx / texWidth);
      const o = shapeRowOffset + row * texWidth * 4 + col * 4;
      pos[o] = shape.posDeltas[d * 3];
      pos[o + 1] = shape.posDeltas[d * 3 + 1];
      // Unity(左手 Z+) → three(右手) 変換に合わせて Z を反転
      pos[o + 2] = -shape.posDeltas[d * 3 + 2];
      nrm[o] = shape.normDeltas[d * 3];
      nrm[o + 1] = shape.normDeltas[d * 3 + 1];
      nrm[o + 2] = -shape.normDeltas[d * 3 + 2];
    }
  }

  const posTex = makeDeltaTexture(pos, texWidth, texHeight);
  const nrmTex = makeDeltaTexture(nrm, texWidth, texHeight);
  return {
    posTex,
    nrmTex,
    count,
    vertexCount,
    texWidth,
    rowsPerShape,
    dispose(): void {
      posTex.dispose();
      nrmTex.dispose();
    },
  };
}

/**
 * シェイプキーの影響度（0〜1）を渡す 1 行テクスチャ。
 * 本体メッシュとアウトラインメッシュで同一インスタンスを共有する。
 */
export function createInfluenceTexture(count: number): {
  data: Float32Array<ArrayBuffer>;
  texture: THREE.DataTexture;
} {
  const n = Math.max(count, 1);
  const data = new Float32Array(new ArrayBuffer(n * 4));
  const texture = new THREE.DataTexture(data, n, 1, THREE.RedFormat, THREE.FloatType);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestFilter;
  texture.generateMipmaps = false;
  texture.flipY = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.needsUpdate = true;
  return { data, texture };
}
